import gsap from 'gsap'
import { MotionPathPlugin } from 'gsap/all'
import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import classNames from 'lib/util/classNames'
import Tooltip from '../Tooltip/Tooltip'
import styles from './ButtonCart.module.scss'

gsap.registerPlugin(MotionPathPlugin)

/**
 * The `ButtonCart`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ButtonCart (props) {
  const { className } = props
  const { setIsCartOpen } = useRootContext()
  const { cartCount, isCartReady } = useUserContext()
  const badgeRef = useRef()
  const classNameOutput = useMemo(
    () =>
      classNames([className, styles.container, isCartReady && styles.ready]),
    [className, isCartReady]
  )
  const lastCartCount = useRef()
  const cartPrettyCount = useMemo(() => {
    if (cartCount < 100) {
      return cartCount
    }

    return '99'
  }, [cartCount])
  const tooltipLabel = useMemo(
    () =>
      `${cartCount.toLocaleString()} ${
        cartCount === 1 ? 'Accessory' : 'Accessories'
      }`,
    [cartCount]
  )

  /**
   * Handle click to open cart
   */
  const handleClick = useCallback(() => {
    setIsCartOpen(wasCartOpen => !wasCartOpen)
  }, [])

  /**
   * Effect: When the `isCartReady` status changes
   */
  useEffect(() => {
    if (!isCartReady) {
      return
    }

    lastCartCount.current = cartCount
  }, [isCartReady])

  /**
   * Effect: When the cart count increases
   */
  useEffect(() => {
    if (!isCartReady) {
      return
    }

    if (cartCount <= lastCartCount.current) {
      lastCartCount.current = cartCount

      return
    }

    lastCartCount.current = cartCount

    gsap.to(badgeRef.current, {
      motionPath: [{ scale: 2 }, { scale: 1 }],
      ease: 'elastic.out(0.5)',
      duration: 2
    })
  }, [cartCount, isCartReady])

  /**
   * Effect: On unmount
   */
  useEffect(() => {
    const badgeEl = badgeRef.current

    return () => {
      gsap.killTweensOf(badgeEl)
    }
  })

  return (
    <Tooltip.Target
      className={classNameOutput}
      onClick={handleClick}
      tooltipContent={
        <Tooltip size="sm">
          <Tooltip.Header>View Cart</Tooltip.Header>
          <Tooltip.Body>{tooltipLabel}</Tooltip.Body>
        </Tooltip>
      }
    >
      <div className={styles.handle} />
      <div className={styles.bag} />
      <div ref={badgeRef} className={styles.badgeWrapper}>
        <div className={styles.badge}>
          <TransitionSwap>{cartPrettyCount}</TransitionSwap>
        </div>
      </div>
    </Tooltip.Target>
  )
}

/** @type {object} */
ButtonCart.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
ButtonCart.defaultProps = {
  className: ''
}

// Memoize
export default memo(ButtonCart)
